import "mapbox-gl/dist/mapbox-gl.css";
import proj4 from "proj4";

import { LngLat, GeoJSONFeature } from "mapbox-gl";
import { RouteAction, RouteActionKind } from "../../../Reducer.ts";
import { Point, RouteState } from "../../../api/edits/editRoute.responses.ts";

// definition utm33 projection
// ALL COORDINATES ARE IN WGS84 EXCEPT WHERE IT WAS EXPLICETELY STATED!
let utm33 =
  'PROJCS["WGS 84 / UTM zone 33N",GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4326"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",15],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",0],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","32633"]]';

// pre:   - route contains route data
//        - selectedFeatures contains the selected MapBox features
//        - selectionPoint is point on which user clicked when selecting
// post:  returns string with selected point in the format of treepanel id
export function TranslateSelection(
  route: RouteState,
  selectedFeatures: GeoJSONFeature[],
  selectionPoint: LngLat,
  altPressed: boolean
): string {
  if (selectedFeatures.length > 0) {
    if (selectedFeatures[0].layer!.id === "points_focused_sounds") {
      for (const fs of route.focusedSounds) {
        if (
          selectedFeatures[0].properties != null &&
          fs.name === selectedFeatures[0].properties.title
        ) {
          return "fss" + fs.id;
        }
      }
    }
    if (selectedFeatures[0].layer!.id === "points_background_sounds") {
      for (const bs of route.backgroundSounds) {
        if (
          selectedFeatures[0].properties != null &&
          bs.name === selectedFeatures[0].properties.title
        ) {
          return "bss" + bs.id;
        }
      }
    }
    if (selectedFeatures[0].layer!.id === "points_POIs") {
      for (let i: number = 0; i < route.pointsOfInterest.length; ++i) {
        if (
          selectedFeatures[0].properties != null &&
          route.pointsOfInterest[i].id ===
            Number(selectedFeatures[0].properties.title)
        ) {
          return "pois" + route.pointsOfInterest[i].id;
        }
      }
    }
    if (selectedFeatures[0].layer!.id === "route_line" && !altPressed) {
      return "rline";
    } else {
      return (
        "rline" +
        String(
          FindClosestPoint(route, {
            latitude: selectionPoint.lat,
            longitude: selectionPoint.lng,
          })
        )
      );
    }
  }
  return "rprop";
}

// pre:   - route contains route data
//        - selectionPoint is point on which user clicked when selecting
// post:  returns index of closest point on route_line
export function FindClosestPoint(
  route: RouteState,
  selectionPoint: Point
): number {
  let minSqDist: number = SquaredDistance(
    proj4(utm33).forward([
      route.linePoints[0].longitude,
      route.linePoints[0].latitude,
    ]),
    proj4(utm33).forward([selectionPoint.longitude, selectionPoint.latitude])
  );
  let minSqDistIndex: number = 0;
  for (let i: number = 0; i < route.linePoints.length; ++i) {
    let dist: number = SquaredDistance(
      proj4(utm33).forward([
        route.linePoints[i].longitude,
        route.linePoints[i].latitude,
      ]),
      proj4(utm33).forward([selectionPoint.longitude, selectionPoint.latitude])
    );
    if (dist < minSqDist) {
      minSqDist = dist;
      minSqDistIndex = i;
    }
  }
  return minSqDistIndex;
}

// pre:   - route has the route with focused sounds
//        - select has the name of the selected entry from treepanel
// post:  returns array data with coordinates of focused sounds
export function MoveFeature(
  routeDispatch: React.Dispatch<RouteAction>,
  movedFeatureName: string,
  movedFeatureLngLat: LngLat,
  movedFeatureOriginLngLat: LngLat
): void {
  // detect layer
  if (movedFeatureName.substring(0, 3) === "bss") {
    let str: string = movedFeatureName.substring(3);
    routeDispatch({
      type: RouteActionKind.MOVE_BACKGROUND,
      payload: {
        id: Number(str),
        pnt: {
          latitude: movedFeatureLngLat.lat,
          longitude: movedFeatureLngLat.lng,
        },
      },
    });
  } else if (movedFeatureName.substring(0, 3) === "fss") {
    let str: string = movedFeatureName.substring(3);
    routeDispatch({
      type: RouteActionKind.MOVE_FOCUSED,
      payload: {
        id: Number(str),
        pnt: {
          latitude: movedFeatureLngLat.lat,
          longitude: movedFeatureLngLat.lng,
        },
      },
    });
  } else if (movedFeatureName.substring(0, 4) === "pois") {
    let str: string = movedFeatureName.substring(4);
    routeDispatch({
      type: RouteActionKind.MOVE_POI,
      payload: {
        id: Number(str),
        pnt: {
          latitude: movedFeatureLngLat.lat,
          longitude: movedFeatureLngLat.lng,
        },
      },
    });
  } else if (movedFeatureName.substring(0, 5) === "rline") {
    let deltaLngLat: LngLat = new LngLat(
      movedFeatureLngLat.lng - movedFeatureOriginLngLat.lng,
      movedFeatureLngLat.lat - movedFeatureOriginLngLat.lat
    );
    if (movedFeatureName.length === 5) {
      routeDispatch({
        type: RouteActionKind.MOVE_ROUTELINE,
        payload: {
          pnt: {
            latitude: deltaLngLat.lat,
            longitude: deltaLngLat.lng,
          },
        },
      });
    } else {
      let str: string = movedFeatureName.substring(5);
      routeDispatch({
        type: RouteActionKind.MOVE_ROUTELINEPOINT,
        payload: {
          id: Number(str),
          pnt: {
            latitude: movedFeatureLngLat.lat,
            longitude: movedFeatureLngLat.lng,
          },
        },
      });
    }
  }
}

// pre:   - route has at least 2 linePoints
//        - queryPnt is the clicked WGS84 point
// post:  - returns insertIndex (splice at this index to place point between
//          the closest segment's two vertices) and distToSegment in meters
export function FindClosestSegmentWithDistance(
  route: RouteState,
  queryPnt: Point
): { insertIndex: number; distToSegment: number } {
  const qUTM = proj4(utm33).forward([queryPnt.longitude, queryPnt.latitude]);
  let minDist = Infinity;
  let bestIndex = 1;

  for (let i = 0; i < route.linePoints.length - 1; i++) {
    const aUTM = proj4(utm33).forward([
      route.linePoints[i].longitude,
      route.linePoints[i].latitude,
    ]);
    const bUTM = proj4(utm33).forward([
      route.linePoints[i + 1].longitude,
      route.linePoints[i + 1].latitude,
    ]);
    const abx = bUTM[0] - aUTM[0];
    const aby = bUTM[1] - aUTM[1];
    const abLenSq = abx * abx + aby * aby;
    let dist: number;
    if (abLenSq === 0) {
      dist = Math.sqrt(SquaredDistance(aUTM, qUTM));
    } else {
      const aqx = qUTM[0] - aUTM[0];
      const aqy = qUTM[1] - aUTM[1];
      const t = Math.max(0, Math.min(1, (aqx * abx + aqy * aby) / abLenSq));
      const cx = aUTM[0] + t * abx;
      const cy = aUTM[1] + t * aby;
      dist = Math.sqrt(
        (qUTM[0] - cx) * (qUTM[0] - cx) + (qUTM[1] - cy) * (qUTM[1] - cy)
      );
    }
    if (dist < minDist) {
      minDist = dist;
      bestIndex = i + 1;
    }
  }
  return { insertIndex: bestIndex, distToSegment: minDist };
}

// pre:   - point1 and point 2 to measure distance
// post:  - squared distance in meters utm33
export function SquaredDistance(point1: number[], point2: number[]): number {
  return (
    (point1[0] - point2[0]) * (point1[0] - point2[0]) +
    (point1[1] - point2[1]) * (point1[1] - point2[1])
  );
}

// pre:   - lngLat with coordinates of the circle center
//        - radius with radius of the circle in meters
// post:  - returns an array with coordinates of the circle polygon
export function CreateCirclePolygon(
  lngLat: number[],
  radius: number
): number[][] {
  // consts for drawing circles
  const NUM_VERTICES: number = radius * 2;

  const DELTA_THETA: number = (2 * Math.PI) / NUM_VERTICES;
  let utm33coord: number[] = proj4(utm33).forward([
    parseFloat(String(lngLat[0])),
    parseFloat(String(lngLat[1])),
  ]);
  let coords: number[][] = [];
  for (let i: number = 0; i <= NUM_VERTICES; i++) {
    let t: number = i * DELTA_THETA;
    let vertex: number[] = proj4(utm33).inverse([
      radius * Math.cos(t) + utm33coord[0],
      radius * Math.sin(t) + utm33coord[1],
    ]);
    coords.push(vertex);
  }
  return coords;
}

// pre:   route objects containtes the route
// post:  cen object contains the coords of the center
export function CenterExtents(
  center: [number, number],
  route: RouteState
): void {
  if (route.linePoints.length !== 0) {
    let minlat: number = route.linePoints[0].latitude;
    let maxlat: number = route.linePoints[0].latitude;
    let minlon: number = route.linePoints[0].longitude;
    let maxlon: number = route.linePoints[0].longitude;
    for (const point of route.linePoints) {
      if (point.latitude < minlat) minlat = point.latitude;
      if (point.latitude > maxlat) maxlat = point.latitude;
      if (point.longitude < minlat) minlon = point.longitude;
      if (point.longitude > maxlat) maxlon = point.longitude;
    }
    center[1] = (minlat + maxlat) / 2.0;
    center[0] = (minlon + maxlon) / 2.0;
  }
}
